import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import type { BookingRuleInfo, TableZoneInfo, BookingSectionInfo } from '@/types/api';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import { getSyncStatusBadge } from '@/utils/syncStatus';

interface RulesTabProps {
  restaurantId: number;
}

export function RulesTab({ restaurantId }: RulesTabProps) {
  const queryClient = useQueryClient();

  const canCreate = useHasPermission(PERM.BOOKING_CREATE);
  const canUpdate = useHasPermission(PERM.BOOKING_UPDATE);
  const canDelete = useHasPermission(PERM.BOOKING_DELETE);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<BookingRuleInfo | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch rules
  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['booking-rules', restaurantId],
    queryFn: () => apiClient.getBookingRules(restaurantId),
    enabled: !!restaurantId,
  });

  // Delete mutation — uuid is the cross-table identifier; backend claims a
  // POS-only synced row into managed before deleting.
  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => apiClient.deleteBookingRule(restaurantId, uuid),
    onSuccess: () => {
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ['booking-rules', restaurantId] });
    },
    onError: (error: any) => {
      setDeleteError(error.response?.data?.message || 'Failed to delete rule');
    },
  });

  const handleDelete = async (uuid: string | undefined) => {
    if (!uuid) return;
    if (window.confirm('Are you sure you want to delete this rule?')) {
      try {
        await deleteMutation.mutateAsync(uuid);
      } catch {
        // surfaced via deleteMutation's onError -> deleteError banner
      }
    }
  };

  const handleEdit = (rule: BookingRuleInfo) => {
    setEditingRule(rule);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingRule(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingRule(null);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['booking-rules', restaurantId] });
    setIsModalOpen(false);
    setEditingRule(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading rules...</div>
      </div>
    );
  }

  return (
    <>
      {deleteError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-center justify-between">
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)} className="text-red-700 hover:text-red-900 font-medium ml-4">
            Dismiss
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {rules.length} {rules.length === 1 ? 'rule' : 'rules'}
        </div>
        {canCreate && (
          <button onClick={handleCreate} className="btn btn-primary h-10">
            <Plus className="h-4 w-4 mr-2" />
            Add Rule
          </button>
        )}
      </div>

      {/* Table */}
      {rules.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-500">No booking rules configured yet.</p>
          <p className="text-sm text-gray-400 mt-1">
            Add a rule to define booking constraints for this restaurant.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Enabled
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Zones
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sections
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sync Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rules.map((rule) => {
                  const badge = getSyncStatusBadge(rule.syncStatus);
                  return (
                    <tr key={rule.uuid ?? rule.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {rule.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {rule.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            rule.enabled
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {rule.enabled ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-[200px] truncate">
                        {rule.description || '\u2014'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {rule.zoneIds.length}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {rule.sectionIds.length}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-1">
                          {canUpdate && (
                            <button
                              onClick={() => handleEdit(rule)}
                              className="text-primary-600 hover:text-primary-900 hover:bg-primary-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                              aria-label={`Edit rule ${rule.name}`}
                            >
                              <Edit2 className="h-5 w-5" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(rule.uuid as unknown as string)}
                              className="text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                              aria-label={`Delete rule ${rule.name}`}
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <RuleModal
          restaurantId={restaurantId}
          rule={editingRule}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
}

// Rule Modal Component for Create/Edit
interface RuleModalProps {
  restaurantId: number;
  rule: BookingRuleInfo | null;
  onClose: () => void;
  onSuccess: () => void;
}

function RuleModal({ restaurantId, rule, onClose, onSuccess }: RuleModalProps) {
  const [formData, setFormData] = useState({
    name: rule?.name || '',
    type: rule?.type || '',
    description: rule?.description || '',
    enabled: rule?.enabled ?? true,
    zoneIds: rule?.zoneIds || ([] as number[]),
    sectionIds: rule?.sectionIds || ([] as number[]),
  });

  const [parameterJsonStr, setParameterJsonStr] = useState(
    rule ? JSON.stringify(rule.parameterJson, null, 2) : '{}'
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch zones for multi-select
  const { data: zones = [] } = useQuery({
    queryKey: ['zones', restaurantId],
    queryFn: () => apiClient.getZones(restaurantId),
    enabled: !!restaurantId,
  });

  // Fetch sections for multi-select
  const { data: sections = [] } = useQuery({
    queryKey: ['booking-sections', restaurantId],
    queryFn: () => apiClient.getBookingSections(restaurantId),
    enabled: !!restaurantId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<BookingRuleInfo>) =>
      apiClient.createBookingRule(restaurantId, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create rule';
      setErrors({ submit: message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<BookingRuleInfo>) =>
      apiClient.updateBookingRule(restaurantId, rule!.uuid as unknown as string, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update rule';
      setErrors({ submit: message });
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Rule name is required';
    }

    if (!formData.type.trim()) {
      newErrors.type = 'Rule type is required';
    }

    try {
      JSON.parse(parameterJsonStr);
    } catch {
      newErrors.parameterJson = 'Invalid JSON format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleZoneToggle = (zoneId: number) => {
    setFormData((prev) => ({
      ...prev,
      zoneIds: prev.zoneIds.includes(zoneId)
        ? prev.zoneIds.filter((id) => id !== zoneId)
        : [...prev.zoneIds, zoneId],
    }));
  };

  const handleSectionToggle = (sectionId: number) => {
    setFormData((prev) => ({
      ...prev,
      sectionIds: prev.sectionIds.includes(sectionId)
        ? prev.sectionIds.filter((id) => id !== sectionId)
        : [...prev.sectionIds, sectionId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const submitData: Partial<BookingRuleInfo> = {
      name: formData.name,
      type: formData.type,
      description: formData.description || undefined,
      enabled: formData.enabled,
      parameterJson: JSON.parse(parameterJsonStr),
      zoneIds: formData.zoneIds,
      sectionIds: formData.sectionIds,
    };

    if (rule) {
      await updateMutation.mutateAsync(submitData);
    } else {
      await createMutation.mutateAsync(submitData);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="card max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {rule ? 'Edit Rule' : 'Add New Rule'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="rule-name" className="block text-sm font-medium text-gray-700 mb-1">
              Rule Name *
            </label>
            <input
              id="rule-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`input w-full ${errors.name ? 'border-red-500' : ''}`}
              placeholder="e.g., Max Party Size, Minimum Notice"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="rule-type" className="block text-sm font-medium text-gray-700 mb-1">
              Type *
            </label>
            <input
              id="rule-type"
              type="text"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className={`input w-full ${errors.type ? 'border-red-500' : ''}`}
              placeholder="e.g., MAX_PARTY_SIZE, MIN_NOTICE_HOURS"
            />
            {errors.type && <p className="text-red-500 text-xs mt-1">{errors.type}</p>}
          </div>

          <div>
            <label htmlFor="rule-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="rule-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input w-full"
              rows={2}
              placeholder="Describe what this rule does"
            />
          </div>

          <div>
            <label htmlFor="rule-parameters" className="block text-sm font-medium text-gray-700 mb-1">
              Parameters (JSON)
            </label>
            <textarea
              id="rule-parameters"
              value={parameterJsonStr}
              onChange={(e) => setParameterJsonStr(e.target.value)}
              className={`input w-full font-mono text-sm ${errors.parameterJson ? 'border-red-500' : ''}`}
              rows={4}
              placeholder='{"key": "value"}'
            />
            {errors.parameterJson && (
              <p className="text-red-500 text-xs mt-1">{errors.parameterJson}</p>
            )}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="rule-enabled"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="rule-enabled" className="ml-2 text-sm text-gray-700">
              Enabled
            </label>
          </div>

          {/* Zone multi-select */}
          {zones.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zones
              </label>
              <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                {zones.map((zone: TableZoneInfo) => (
                  <div key={zone.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`rule-zone-${zone.id}`}
                      checked={formData.zoneIds.includes(zone.id)}
                      onChange={() => handleZoneToggle(zone.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={`rule-zone-${zone.id}`}
                      className="ml-2 text-sm text-gray-700"
                    >
                      {zone.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section multi-select */}
          {sections.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sections
              </label>
              <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                {sections.map((section: BookingSectionInfo) => (
                  <div key={section.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`rule-section-${section.id}`}
                      checked={formData.sectionIds.includes(section.id)}
                      onChange={() => handleSectionToggle(section.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={`rule-section-${section.id}`}
                      className="ml-2 text-sm text-gray-700"
                    >
                      {section.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="btn btn-outline flex-1 h-10">
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1 h-10"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : rule
                ? 'Update Rule'
                : 'Create Rule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RulesTab;